/**
 * Portfolio Site Map — Polygon dashboard.
 *
 *   1. State boundary lines drawn from a GeoJSON layer (no country labels,
 *      no place names) on top of the satellite basemap. County lines are
 *      lazy-loaded when the user zooms past `COUNTY_MIN_ZOOM`.
 *   2. Always-on site labels (permanent leaflet Tooltips) so dots are
 *      identifiable without hovering. Suppressed below LABEL_MIN_ZOOM to
 *      reduce overlap when the whole US is in view.
 *   3. Filter bar above the map (status / contaminant / aquifer). Dropdowns
 *      seed from a curated list of common values AND any values present in
 *      the data, deduped — so common environmental filters like TPH and
 *      Ogallala are always selectable even if no site reports them yet.
 *      Match is case-insensitive substring so "BTEX" matches "Total BTEX",
 *      "TPH" matches "TPH-GRO", "Ogallala" matches "Ogallala Aquifer", etc.
 *   4. Status filter pulls from v3.2 site_trend (improving / worsening /
 *      stable / mixed) and falls back to portfolio priority_category
 *      (critical / high / moderate / low).
 */
import { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapContainer, Marker, Tooltip, GeoJSON, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import type { FeatureCollection } from 'geojson';
import 'leaflet/dist/leaflet.css';
import { EsriVectorBasemap } from '@/lib/basemap';
import { CtrlScrollZoom } from './CtrlScrollZoom';

export interface MapSite {
  id: string;
  name: string;
  latitude: number | null;
  longitude: number | null;
  threat_tier?: number;
  /** v3.2 site_trend — improving | worsening | stable | mixed */
  site_trend?: string | null;
  /** portfolio ranking bucket — critical | high | moderate | low */
  priority_category?: string | null;
  /** plain-language status to filter by (derived from the two above) */
  status?: string | null;
  /** primary contaminant the site reports (Site.primary_contaminant) */
  primary_contaminant?: string | null;
  /** aquifer / formation name (Site.aquifer_type) */
  aquifer?: string | null;
  // v3.2 long-horizon signal — present once the inference pipeline writes
  // the new SiteConcentrationSummary aggregates.
  tier_v32?: number | null;
  tier_confidence?: string | null;
  /** Latest horizon where any well still exceeds MCL (3mo / 1yr / 2yr / 5yr / 10yr / 20yr) */
  concern_horizon_label?: string | null;
  trajectory_shape?: string | null;
}

interface PortfolioSiteMapProps {
  sites: MapSite[];
}

const TIER_COLORS: Record<number, { color: string; label: string }> = {
  1: { color: '#ef4444', label: 'Critical' },
  2: { color: '#f97316', label: 'High' },
  3: { color: '#eab308', label: 'Moderate' },
  4: { color: '#22c55e', label: 'Low' },
  5: { color: '#06b6d4', label: 'Minimal' },
};

const LABEL_MIN_ZOOM = 6;          // permanent site labels appear at/above this zoom
const COUNTY_MIN_ZOOM = 7;         // county boundaries appear at/above this zoom
const COUNTY_LABEL_MIN_ZOOM = 8;   // county NAME labels appear at/above this zoom

// Curated filter options — always present in the dropdowns even when no site
// in the loaded data reports them. Filter logic does case-insensitive
// substring match so these synonyms catch the obvious variants.
//
// The v3.2 long-horizon options ("Exceeds at 20yr", "Closes within 5yr",
// "Trajectory: worsening", etc.) live under matchesLongHorizon() and are
// checked separately so they can act on tier_v32 / concern_horizon_label /
// trajectory_shape instead of the plain `status` substring.
const COMMON_STATUSES = [
  'critical', 'high', 'moderate', 'low',
  'worsening', 'increasing',
  'improving', 'decreasing',
  'stable', 'mixed',
  // v3.2 long-horizon options — handled by matchesLongHorizon below.
  'v3.2: exceeds at 20yr',
  'v3.2: exceeds at 10yr',
  'v3.2: exceeds at 5yr',
  'v3.2: closes within 5yr',
  'v3.2: closes within 2yr',
  'v3.2: clean today',
  'v3.2: trajectory worsening',
  'v3.2: trajectory converging',
  'v3.2: trajectory spiking-recovering',
  'v3.2: trajectory plateau',
];
const COMMON_CONTAMINANTS = [
  'Benzene', 'Toluene', 'Ethylbenzene', 'Xylenes', 'Total BTEX',
  'MTBE', 'Naphthalene',
  'TPH', 'TPH-GRO', 'TPH-DRO', 'C6-C12', 'C12-C28', 'C28-C35',
  'PCE', 'TCE',
];
const COMMON_AQUIFERS = [
  'Ogallala', 'Dockum', 'Edwards', 'Edwards-Trinity', 'Trinity',
  'Carrizo-Wilcox', 'Gulf Coast', 'Pecos Valley', 'Seymour',
  'Hueco-Mesilla Bolsons', 'Cenozoic Pecos Alluvium', 'Blossom',
];

// Hosted GeoJSON sources — small enough to fetch at component mount.
// Cached at module scope so they only fetch once across mounts.
const STATES_URL = 'https://cdn.jsdelivr.net/gh/PublicaMundi/MappingAPI@master/data/geojson/us-states.json';
const COUNTIES_URL = 'https://cdn.jsdelivr.net/gh/plotly/datasets@master/geojson-counties-fips.json';
let statesCache: FeatureCollection | null = null;
let countiesCache: FeatureCollection | null = null;

function makeTierIcon(tier: number): L.DivIcon {
  const { color } = TIER_COLORS[tier] || TIER_COLORS[3];
  return L.divIcon({
    className: '',
    html: `<div style="
      width: 12px;
      height: 12px;
      background: ${color};
      border: 2px solid #fff;
      border-radius: 50%;
      box-shadow: 0 1px 4px rgba(0,0,0,0.5);
    "></div>`,
    iconSize: [12, 12],
    iconAnchor: [6, 6],
  });
}

const TIER_ICONS: Record<number, L.DivIcon> = {
  1: makeTierIcon(1), 2: makeTierIcon(2), 3: makeTierIcon(3),
  4: makeTierIcon(4), 5: makeTierIcon(5),
};

function siteStatus(s: MapSite): string {
  return (s.status || s.site_trend || s.priority_category || 'unknown').toLowerCase();
}

/** Map tier_v32 (1-5) to the tier color we already use for threat_tier. */
function tierFromV32(s: MapSite): number {
  if (typeof s.tier_v32 === 'number' && s.tier_v32 >= 1 && s.tier_v32 <= 5) return s.tier_v32;
  return s.threat_tier || 5;
}

/** Long-horizon filter — returns true if the site matches a `v3.2:` option,
 *  false if it doesn't, or null if the selected option isn't a v3.2 one (in
 *  which case the caller falls back to the plain status substring match). */
function matchesLongHorizon(s: MapSite, selected: string): boolean | null {
  if (!selected.startsWith('v3.2:')) return null;
  const opt = selected.slice('v3.2:'.length).trim().toLowerCase();

  // Tier-based options.
  if (opt === 'exceeds at 20yr') return s.tier_v32 === 1;
  if (opt === 'exceeds at 10yr') return s.tier_v32 === 1 || s.tier_v32 === 2;
  if (opt === 'exceeds at 5yr') return (s.tier_v32 ?? 99) <= 3;
  if (opt === 'closes within 5yr') return (s.tier_v32 ?? 0) >= 4;
  if (opt === 'closes within 2yr') return s.tier_v32 === 4 || s.tier_v32 === 5;
  if (opt === 'clean today') return s.tier_v32 === 5;

  // Trajectory-shape options.
  if (opt.startsWith('trajectory ')) {
    const want = opt.slice('trajectory '.length).replace('-', '_');
    return (s.trajectory_shape || '').toLowerCase() === want;
  }

  return false;
}

/** Case-insensitive substring match. Returns true if `value` contains
 *  `selected`, or `selected` contains `value`, so "TPH" matches both
 *  "TPH-GRO" and "TPH". */
function matchesFilter(value: string | null | undefined, selected: string): boolean {
  if (selected === 'all') return true;
  const v = (value || '').toLowerCase();
  const s = selected.toLowerCase();
  return v.includes(s) || s.includes(v);
}

function dedupeMerge(curated: string[], discovered: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const x of curated) {
    const k = x.toLowerCase();
    if (!seen.has(k)) { seen.add(k); out.push(x); }
  }
  for (const x of discovered) {
    const k = x.toLowerCase();
    if (!seen.has(k)) { seen.add(k); out.push(x); }
  }
  return out;
}

function ZoomWatcher({ onZoom }: { onZoom: (z: number) => void }) {
  useMapEvents({
    zoomend: e => onZoom(e.target.getZoom()),
  });
  return null;
}

export function PortfolioSiteMap({ sites }: PortfolioSiteMapProps) {
  const navigate = useNavigate();
  const [zoom, setZoom] = useState(4);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [contaminantFilter, setContaminantFilter] = useState<string>('all');
  const [aquiferFilter, setAquiferFilter] = useState<string>('all');

  const [statesGeo, setStatesGeo] = useState<FeatureCollection | null>(statesCache);
  const [countiesGeo, setCountiesGeo] = useState<FeatureCollection | null>(countiesCache);

  // Lazy fetch states GeoJSON once on first mount.
  useEffect(() => {
    if (statesCache) return;
    let cancelled = false;
    fetch(STATES_URL)
      .then(r => r.ok ? r.json() : null)
      .then((g: FeatureCollection | null) => {
        if (cancelled || !g) return;
        statesCache = g;
        setStatesGeo(g);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // County GeoJSON is ~3MB so we hold off until the user actually zooms in.
  useEffect(() => {
    if (zoom < COUNTY_MIN_ZOOM) return;
    if (countiesCache) {
      if (!countiesGeo) setCountiesGeo(countiesCache);
      return;
    }
    let cancelled = false;
    fetch(COUNTIES_URL)
      .then(r => r.ok ? r.json() : null)
      .then((g: FeatureCollection | null) => {
        if (cancelled || !g) return;
        countiesCache = g;
        setCountiesGeo(g);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [zoom, countiesGeo]);

  // One-time CSS — dark pill tooltip for site labels; transparent text-only
  // labels for state and county names (no pill, just outlined glyphs).
  useEffect(() => {
    const id = 'polygon-portfolio-map-tooltip-css';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      .leaflet-tooltip.portfolio-site-label {
        background: rgba(10, 10, 10, 0.85);
        border: 1px solid rgba(64, 64, 64, 0.8);
        color: #e5e7eb;
        font-size: 10px;
        font-weight: 500;
        padding: 1px 5px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.6);
        white-space: nowrap;
      }
      .leaflet-tooltip.portfolio-site-label::before { display: none; }

      .leaflet-tooltip.portfolio-state-label {
        background: transparent;
        border: none;
        box-shadow: none;
        color: #ffffff;
        font-size: 7px;
        font-weight: 700;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        text-shadow:
          -1px -1px 0 rgba(0,0,0,0.85),
          1px -1px 0 rgba(0,0,0,0.85),
          -1px 1px 0 rgba(0,0,0,0.85),
          1px 1px 0 rgba(0,0,0,0.85),
          0 0 4px rgba(0,0,0,0.6);
        padding: 0;
        pointer-events: none;
      }
      .leaflet-tooltip.portfolio-state-label::before { display: none; }

      .leaflet-tooltip.portfolio-county-label {
        background: transparent;
        border: none;
        box-shadow: none;
        color: #f3f4f6;
        font-size: 10px;
        font-weight: 500;
        text-shadow:
          -1px -1px 0 rgba(0,0,0,0.85),
          1px -1px 0 rgba(0,0,0,0.85),
          -1px 1px 0 rgba(0,0,0,0.85),
          1px 1px 0 rgba(0,0,0,0.85);
        padding: 0;
        pointer-events: none;
      }
      .leaflet-tooltip.portfolio-county-label::before { display: none; }
    `;
    document.head.appendChild(style);
  }, []);

  const geoSites = useMemo(
    () => sites.filter((s) => s.latitude != null && s.longitude != null && s.latitude !== 0 && s.longitude !== 0),
    [sites],
  );

  // Build dropdown options — curated common values merged with whatever's
  // actually present in the loaded data, deduped case-insensitively.
  const statuses = useMemo(() => {
    const found = new Set<string>();
    for (const s of geoSites) {
      const st = siteStatus(s);
      if (st && st !== 'unknown') found.add(st);
    }
    return dedupeMerge(COMMON_STATUSES, Array.from(found).sort());
  }, [geoSites]);

  const contaminants = useMemo(() => {
    const found = new Set<string>();
    for (const s of geoSites) if (s.primary_contaminant) found.add(s.primary_contaminant);
    return dedupeMerge(COMMON_CONTAMINANTS, Array.from(found).sort());
  }, [geoSites]);

  const aquifers = useMemo(() => {
    const found = new Set<string>();
    for (const s of geoSites) if (s.aquifer) found.add(s.aquifer);
    return dedupeMerge(COMMON_AQUIFERS, Array.from(found).sort());
  }, [geoSites]);

  const filteredSites = useMemo(() => {
    return geoSites.filter(s => {
      // Status: try v3.2 long-horizon match first; if the option isn't a
      // v3.2 one, fall back to the substring match against site_trend /
      // priority_category.
      const longHorizon = matchesLongHorizon(s, statusFilter);
      if (longHorizon === false) return false;
      if (longHorizon === null && !matchesFilter(siteStatus(s), statusFilter)) return false;
      if (!matchesFilter(s.primary_contaminant, contaminantFilter)) return false;
      if (!matchesFilter(s.aquifer, aquiferFilter)) return false;
      return true;
    });
  }, [geoSites, statusFilter, contaminantFilter, aquiferFilter]);

  const labelsVisible = zoom >= LABEL_MIN_ZOOM;
  const showCounties = zoom >= COUNTY_MIN_ZOOM && countiesGeo;

  return (
    <div className="rounded-lg overflow-hidden border border-mono-800 bg-mono-900">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-mono-800 text-[11px]">
        <span className="text-mono-500 uppercase tracking-wider mr-1">Filters:</span>
        <FilterSelect label="Status" value={statusFilter} options={statuses} onChange={setStatusFilter} />
        <FilterSelect label="Contaminant" value={contaminantFilter} options={contaminants} onChange={setContaminantFilter} />
        <FilterSelect label="Aquifer" value={aquiferFilter} options={aquifers} onChange={setAquiferFilter} />
        <button
          onClick={() => { setStatusFilter('all'); setContaminantFilter('all'); setAquiferFilter('all'); }}
          className="ml-1 px-2 py-0.5 rounded border border-mono-700 bg-mono-800 text-mono-400 hover:text-mono-200"
        >
          Clear
        </button>
        <span className="ml-auto text-mono-500">
          {filteredSites.length}/{geoSites.length} sites · labels {labelsVisible ? 'on' : `at zoom ${LABEL_MIN_ZOOM}+`}
          {showCounties ? ' · counties on' : ''}
        </span>
      </div>

      <div style={{ height: 760 }}>
        <MapContainer
          center={[39.5, -98.5]}
          zoom={4}
          style={{ height: '100%', width: '100%' }}
          zoomControl={true}
          attributionControl={false}
          scrollWheelZoom={false}
        >
          <CtrlScrollZoom />
          <ZoomWatcher onZoom={setZoom} />

          {/* Esri vector basemap (satellite + labels via MapLibre). */}
          <EsriVectorBasemap />

          {/* State boundary lines + permanent name labels at the polygon
              centroid. Lines are white so they read against the imagery
              basemap without competing with the amber per-tile labels we
              used to inherit from the Esri reference overlay. */}
          {statesGeo && (
            <GeoJSON
              key="states"
              data={statesGeo}
              style={{
                color: '#ffffff',
                weight: 1.4,
                fillOpacity: 0,
                opacity: 0.85,
              }}
              onEachFeature={(feature, layer) => {
                const name = (feature.properties as { name?: string; NAME?: string } | null)?.name
                  ?? (feature.properties as { NAME?: string } | null)?.NAME
                  ?? '';
                if (!name) return;
                layer.bindTooltip(name, {
                  permanent: true,
                  direction: 'center',
                  className: 'portfolio-state-label',
                  sticky: false,
                });
              }}
              interactive={false}
            />
          )}

          {/* County lines (lazy-loaded, only at higher zoom). White-grey,
              thinner than state lines so the visual hierarchy reads
              correctly. County name labels appear one zoom level later
              to keep the screen readable. */}
          {showCounties && countiesGeo && (
            <GeoJSON
              key={`counties-${zoom >= COUNTY_LABEL_MIN_ZOOM ? 'labeled' : 'plain'}`}
              data={countiesGeo}
              style={{
                color: '#ffffff',
                weight: 0.7,
                fillOpacity: 0,
                opacity: 0.55,
                dashArray: '2 3',
              }}
              onEachFeature={(feature, layer) => {
                if (zoom < COUNTY_LABEL_MIN_ZOOM) return;
                const props = feature.properties as { NAME?: string; name?: string } | null;
                const name = props?.NAME ?? props?.name ?? '';
                if (!name) return;
                layer.bindTooltip(name, {
                  permanent: true,
                  direction: 'center',
                  className: 'portfolio-county-label',
                  sticky: false,
                });
              }}
              interactive={false}
            />
          )}

          {filteredSites.map((site) => {
            // Prefer v3.2-derived tier when present; fall back to the static
            // Site.threat_tier so dots have a colour even before inference.
            const tier = tierFromV32(site);
            const tierInfo = TIER_COLORS[tier] || TIER_COLORS[5];
            return (
              <Marker
                key={site.id}
                position={[site.latitude!, site.longitude!]}
                icon={TIER_ICONS[tier] || TIER_ICONS[5]}
                eventHandlers={{
                  click: () => navigate(`/polygon/sites/${site.id}`),
                }}
              >
                {labelsVisible ? (
                  <Tooltip
                    direction="bottom"
                    offset={[0, 8]}
                    permanent
                    className="portfolio-site-label"
                  >
                    {site.name}
                  </Tooltip>
                ) : (
                  <Tooltip direction="top" offset={[0, -8]}>
                    <span style={{ fontWeight: 600 }}>{site.name}</span>
                    {site.threat_tier && (
                      <>
                        {' — '}
                        <span>T{tier} {tierInfo.label}</span>
                      </>
                    )}
                  </Tooltip>
                )}
              </Marker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}

interface FilterSelectProps {
  label: string;
  value: string;
  options: string[];
  onChange: (v: string) => void;
}

function FilterSelect({ label, value, options, onChange }: FilterSelectProps) {
  return (
    <label className="flex items-center gap-1 text-mono-400">
      <span className="text-mono-500">{label}:</span>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="bg-mono-800 border border-mono-700 rounded px-1.5 py-0.5 text-mono-200 focus:border-cyan-500 focus:outline-none max-w-[180px]"
      >
        <option value="all">All</option>
        {options.map(o => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </label>
  );
}
